import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_LAYOUT, isThemeLayoutKey, type ThemeLayoutKey } from "@/lib/themeLayouts";
import { PREVIEW_PARAM, PREVIEW_STORAGE_KEY, tokensToCss } from "@/lib/themeTokens";
import { resolveLiveTheme, type LiveTheme } from "@/lib/themeSchedule";

type SiteThemeState = { layout: ThemeLayoutKey; previewName: string | null; ready: boolean };
const SiteThemeContext = createContext<SiteThemeState>({ layout: DEFAULT_LAYOUT, previewName: null, ready: false });
export const useSiteTheme = () => useContext(SiteThemeContext);

export function SiteThemeProvider({ children, initialTheme }: { children: ReactNode; initialTheme: LiveTheme | null }) {
  const [mounted, setMounted] = useState(false);
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);
  useEffect(() => {
    const fromUrl = new URL(window.location.href).searchParams.get(PREVIEW_PARAM);
    try {
      if (fromUrl === "off") sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
      else if (fromUrl) sessionStorage.setItem(PREVIEW_STORAGE_KEY, fromUrl);
      setPreviewSlug(fromUrl === "off" ? null : fromUrl || sessionStorage.getItem(PREVIEW_STORAGE_KEY));
    } catch {
      setPreviewSlug(fromUrl === "off" ? null : fromUrl);
    }
    setMounted(true);
  }, []);

  const themeQuery = useQuery({
    queryKey: ["site-theme-display", previewSlug],
    enabled: mounted,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<LiveTheme | null> => {
      const { supabase } = await import("@/integrations/supabase/client");
      if (previewSlug) {
        const { data, error } = await supabase.from("site_themes").select("slug,name,layout,tokens").eq("slug", previewSlug).maybeSingle();
        if (error) throw error;
        if (data) return data as LiveTheme;
      }
      return resolveLiveTheme(supabase);
    },
  });
  const ready = !mounted || themeQuery.isSuccess || themeQuery.isError;
  const theme = themeQuery.isSuccess ? themeQuery.data : initialTheme;
  const layout = theme && isThemeLayoutKey(theme.layout) ? theme.layout : DEFAULT_LAYOUT;
  const css = tokensToCss(theme?.tokens ?? {});

  return (
    <SiteThemeContext.Provider value={{ layout, ready, previewName: previewSlug && theme ? theme.name : null }}>
      {css && <style data-site-theme-tokens dangerouslySetInnerHTML={{ __html: css }} />}
      {children}
    </SiteThemeContext.Provider>
  );
}